import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { getPublicMenuUrl } from "@/services/tables/tableService";
import { submitOrder } from "@/services/orders";
import { Loader2 } from "lucide-react";

export default function PaymentCallback() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(true);
  const hasProcessed = useRef(false);

  useEffect(() => {
    const handlePaymentCallback = async () => {
      // Prevent double processing
      if (hasProcessed.current) {
        return;
      }
      hasProcessed.current = true;

      // Get URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const tableId = urlParams.get("table");
      const status = urlParams.get("status");
      const sessionId = urlParams.get("session_id");
      const orderId = urlParams.get("order_id");

      if (!tableId) {
        throw new Error("No table ID provided in callback URL");
      }

      // Handle payment cancellation
      if (status === 'cancelled') {
        try {
          // Get the pending order data from localStorage
          const pendingOrderData = localStorage.getItem("pendingOrder");
          
          if (pendingOrderData) {
            const pendingOrder = JSON.parse(pendingOrderData);
            
            // Store cart data for restoration
            const cartData = {
              cartItems: pendingOrder.cartItems,
              specialInstructions: pendingOrder.specialInstructions,
              selectedTip: pendingOrder.tipPercentage || 0,
              timestamp: new Date().toISOString()
            };
            
            // Save cart data to localStorage for restoration
            localStorage.setItem(`cart_${tableId}`, JSON.stringify(cartData));
            
            // Clear the pending order
            localStorage.removeItem("pendingOrder");
          }
        } catch (error) {
          console.error("Error handling payment cancellation:", error);
        }

        // Show cancellation message
        toast({
          title: t("paymentCancelled", "Payment Cancelled"),
          description: t("paymentCancelledMessage", "Your payment was cancelled. Your cart has been preserved."),
          variant: "default",
        });

        // Navigate back to menu with cart restoration flag
        const isDev = process.env.NODE_ENV === "development";
        const baseUrl = isDev ? `/dashboard/menu-sandbox` : `/menu`;
        navigate(`${baseUrl}?table=${tableId}&restoreCart=true&showCashSuggestion=true`);
        return;
      }

      try {
        // Get the pending order data from localStorage first
        let pendingOrderData;
        try {
          pendingOrderData = localStorage.getItem("pendingOrder");

          if (!pendingOrderData) {
            throw new Error("No pending order found");
          }
        } catch (storageError) {
          throw new Error("Failed to access order data");
        }

        let pendingOrder;
        try {
          pendingOrder = JSON.parse(pendingOrderData);

          // Validate the parsed data
          if (
            !pendingOrder.cartItems ||
            !pendingOrder.storeId ||
            !pendingOrder.tableId
          ) {
            throw new Error("Invalid pending order data");
          }
        } catch (parseError) {
          throw new Error("Failed to parse order data");
        }

        // Create the order items
        const orderItems = pendingOrder.cartItems.map((item: {
          menuItemId: string;
          menuItemName: string;
          quantity: number;
          price: number;
          imageUrl?: string;
        }) => ({
          id: crypto.randomUUID(),
          order_id: orderId || crypto.randomUUID(),
          menuItemId: item.menuItemId,
          menuItemName: item.menuItemName,
          quantity: item.quantity,
          price: item.price,
          image_url: item.imageUrl,
        }));

        let isPaymentSuccessful = false;
        let metadataOrderId = orderId;

        // Handle Stripe payment (session_id indicates Stripe)
        if (sessionId) {
          // For Stripe, the webhook handles payment verification
          // We just need to check if the order was marked as paid
          if (status === 'success') {
            isPaymentSuccessful = true;
          }
        } else {
          // Handle Paystack payment
          const reference = urlParams.get("reference");
          const trxref = urlParams.get("trxref");

          if (!reference || !trxref) {
            throw new Error("Invalid payment response");
          }

          const verifyResponse = await fetch(
            `https://api.paystack.co/transaction/verify/${reference}`,
            {
              headers: {
                Authorization: `Bearer ${"sk_test_ce6655a44099dce81885d88929ce35bb426a100a"}`,
              },
            }
          );

          const verifyData = await verifyResponse.json();

          // Extract metadata from the verification response
          const { order_id: paystackOrderId, store_id: storeId } =
            verifyData.data.metadata;
          
          metadataOrderId = paystackOrderId;

          // Handle failed payment verification
          if (!verifyResponse.ok || !verifyData.status) {
            throw new Error("Payment verification failed");
          }

          isPaymentSuccessful = true;
        }

        // Submit the order
        const result = await submitOrder(
          {
            id: metadataOrderId,
            storeId: pendingOrder.storeId,
            tableId: pendingOrder.tableId,
            tableNumber: pendingOrder.tableNumber,
            tableName: pendingOrder.tableName,
            status: "pending",
            totalAmount: pendingOrder.totalAmount,
            specialInstructions:
              pendingOrder.specialInstructions?.trim() || undefined,
            payment_method: "Digital",
            is_payed: isPaymentSuccessful,
            tip_amount: pendingOrder.tipAmount || 0,
            tip_percentage: pendingOrder.tipPercentage || 0,
          },
          orderItems
        );

        if (!result) {
          throw new Error("Failed to create order");
        }

        // Clear the pending order from localStorage
        try {
          localStorage.removeItem("pendingOrder");
        } catch (clearError) {
          console.error("Error clearing pending order:", clearError);
        }

        if (isPaymentSuccessful) {
          toast({
            title: t("success"),
            description: t("paymentSuccessful"),
          });
        } else {
          // Payment failed - restore cart data
          const cartData = {
            cartItems: pendingOrder.cartItems,
            specialInstructions: pendingOrder.specialInstructions,
            selectedTip: pendingOrder.tipPercentage || 0,
            timestamp: new Date().toISOString()
          };
          
          localStorage.setItem(`cart_${tableId}`, JSON.stringify(cartData));
          
          toast({
            title: t("paymentFailed"),
            description: t("paymentFailedMessage"),
            variant: "destructive",
          });

          // Navigate back to menu with cart restoration and cash suggestion flags
          const isDev = process.env.NODE_ENV === "development";
          const baseUrl = isDev ? `/dashboard/menu-sandbox` : `/menu`;
          navigate(`${baseUrl}?table=${tableId}&restoreCart=true&showCashSuggestion=true`);
          return;
        }

        // Navigate back to the menu page
        const isDev = process.env.NODE_ENV === "development";
        if (isDev) {
          navigate(`/dashboard/menu-sandbox?table=${tableId}`);
        } else {
          navigate(`/menu?table=${tableId}`);
        }
      } catch (error) {
        // On error, try to restore cart data if available
        try {
          const pendingOrderData = localStorage.getItem("pendingOrder");
          if (pendingOrderData) {
            const pendingOrder = JSON.parse(pendingOrderData);
            const cartData = {
              cartItems: pendingOrder.cartItems,
              specialInstructions: pendingOrder.specialInstructions,
              selectedTip: pendingOrder.tipPercentage || 0,
              timestamp: new Date().toISOString()
            };
            
            localStorage.setItem(`cart_${tableId}`, JSON.stringify(cartData));
          }
        } catch (restoreError) {
          console.error("Error restoring cart data:", restoreError);
        }

        toast({
          title: t("error"),
          description:
            error instanceof Error
              ? error.message
              : t("paymentVerificationFailed"),
          variant: "destructive",
        });

        // On error, redirect to the menu page with cart restoration flag
        const isDev = process.env.NODE_ENV === "development";
        const baseUrl = isDev ? `/dashboard/menu-sandbox` : `/menu`;
        navigate(`${baseUrl}?table=${tableId}&restoreCart=true&showCashSuggestion=true`);
      } finally {
        setIsProcessing(false);
      }
    };

    handlePaymentCallback();
  }, [navigate, toast, t]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
        <h1 className="text-2xl font-bold">{t("processingPayment")}</h1>
        <p className="text-muted-foreground">{t("pleaseWait")}</p>
      </div>
    </div>
  );
}
